import { type ChangeEvent, useRef, useState } from "react";
import { Download, FileText, Paperclip, Trash2, Upload } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import DropZoneFeedback from "@cle/components/DropZoneFeedback";
import RoundedCard from "@cle/components/RoundedCard";
import { isPdfFile, useFileDropZone } from "@cle/hooks/useFileDropZone";
import { mergeClass } from "@cle/utils/mergeClass";
import {
  MAX_ATTACHMENTS,
  SUPPLEMENTARY_DOCUMENTATION_TEXTS,
  canDeleteAttachments,
  getAttachmentsClosedMessage,
  canUploadMoreAttachments,
  getAttachmentRejectionMessage,
  getAttachments,
  resolveAttachmentRejection,
} from "@cle/utils/supplementaryDocumentation";
import { getCleUploadErrorMessage } from "@cle/utils/getCleUploadErrorMessage";
import type {
  SupplementableEvidence,
  SupplementaryDocumentKind,
} from "@cle/models/evidenceAttachment";
import {
  useDeleteEvidenceAttachment,
  useDownloadEvidenceAttachment,
  useUploadEvidenceAttachment,
} from "@/hooks/useEvidenceAttachments";
import { formatDanishDateTime } from "@/util/date";

type SupplementaryDocumentationCardProps = {
  evidence: SupplementableEvidence;
  kind: SupplementaryDocumentKind;
};

const INVALID_DROP_MESSAGE = "Bilag skal være en PDF.";

/**
 * Bilag til et kursusbevis eller en undervisning: det materiale, der
 * understøtter et bevis, systemet eller sekretariatet skal tage stilling til.
 *
 * Kortet er det samme for begge (ADV-359); kun ordene følger beviset, se
 * SUPPLEMENTARY_DOCUMENTATION_TEXTS. Noten under knappen siger med vilje, at
 * en upload giver sekretariatet besked, og hvor svaret kommer.
 */
const SupplementaryDocumentationCard = ({
  evidence,
  kind,
}: SupplementaryDocumentationCardProps) => {
  const texts = SUPPLEMENTARY_DOCUMENTATION_TEXTS[kind];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  const attachments = getAttachments(evidence);
  const canUpload = canUploadMoreAttachments(evidence);
  const canDelete = canDeleteAttachments(evidence);

  const uploadMutation = useUploadEvidenceAttachment(kind);
  const deleteMutation = useDeleteEvidenceAttachment(kind);
  const downloadMutation = useDownloadEvidenceAttachment(kind);

  const handleFiles = (files: File[]) => {
    const [file] = files;

    if (!file) {
      return;
    }

    // Ét bilag ad gangen: hvert bilag skal kunne slettes for sig, og en
    // delvist lykkedes flerfil-upload er en tilstand, kortet ikke kan forklare.
    const rejection = resolveAttachmentRejection(file, attachments.length);

    if (rejection) {
      setRejectionMessage(getAttachmentRejectionMessage(rejection, kind));

      return;
    }

    setRejectionMessage(null);
    uploadMutation.mutate({ evidenceId: evidence.id, file });
  };

  const { dropError, dropZoneProps, isDragActive } = useFileDropZone({
    acceptFile: isPdfFile,
    disabled: !canUpload || uploadMutation.isPending,
    invalidDropMessage: INVALID_DROP_MESSAGE,
    onFilesDrop: handleFiles,
  });

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    // Nulstil synkront, ellers udløser det samme filvalg ingen change-event
    // næste gang.
    event.target.value = "";
    handleFiles(files);
  };

  return (
    <RoundedCard
      className={mergeClass(
        "cle-course-certificate-document__side-card",
        canUpload && "cle-rounded-card--drop-target",
        canUpload && isDragActive && "cle-rounded-card--drag-active",
      )}
      header={
        <span className="d-inline-flex align-items-center gap-2">
          <Paperclip size={20} aria-hidden strokeWidth={1.9} />
          Supplerende dokumentation
        </span>
      }
      aria-busy={uploadMutation.isPending}
      {...(canUpload ? dropZoneProps : {})}
    >
      <DropZoneFeedback
        actionError={
          rejectionMessage ??
          (uploadMutation.isError
            ? getCleUploadErrorMessage(
                uploadMutation.error,
                "Bilaget kunne ikke uploades lige nu. Prøv igen.",
              )
            : null) ??
          (deleteMutation.isError
            ? "Bilaget kunne ikke fjernes lige nu. Prøv igen."
            : null) ??
          (downloadMutation.isError
            ? "Bilaget kunne ikke hentes lige nu. Prøv igen."
            : null)
        }
        actionStatus={uploadMutation.isPending ? "Uploader bilag..." : null}
        dropError={dropError}
        dropMessage="Slip for at vedhæfte"
        isDragActive={canUpload && isDragActive}
      />

      {attachments.length === 0 ? (
        <p className="text-muted mb-0 small">{texts.emptyIntro}</p>
      ) : (
        <ul className="cle-course-certificate-document__attachment-list">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="cle-course-certificate-document__attachment"
            >
              <span
                className="cle-course-certificate-document__attachment-icon"
                aria-hidden
              >
                <FileText size={18} strokeWidth={1.75} />
              </span>
              <div className="cle-course-certificate-document__attachment-body">
                <p className="cle-course-certificate-document__attachment-name mb-0">
                  {attachment.fileName}
                </p>
                <p className="text-muted small mb-0">
                  {formatDanishDateTime(attachment.createdAt)}
                </p>
              </div>
              <CleButton
                id={`cle-download-attachment-${attachment.id}`}
                variant="ghost"
                size="icon"
                aria-label={`Hent ${attachment.fileName}`}
                disabled={downloadMutation.isPending}
                onClick={() =>
                  downloadMutation.mutate({
                    evidenceId: evidence.id,
                    attachmentId: attachment.id,
                    fileName: attachment.fileName,
                  })
                }
              >
                <Download size={16} aria-hidden strokeWidth={1.75} />
              </CleButton>
              {canDelete ? (
                <ConfirmationModal
                  title="Fjern bilag"
                  description="Bilaget slettes permanent. Handlingen kan ikke fortrydes."
                  confirmLabel="Fjern"
                  confirmVariant="destructive"
                  isConfirming={deleteMutation.isPending}
                  onConfirm={({ close }) =>
                    deleteMutation.mutate(
                      {
                        evidenceId: evidence.id,
                        attachmentId: attachment.id,
                      },
                      { onSuccess: close },
                    )
                  }
                  trigger={
                    <CleButton
                      id={`cle-delete-attachment-${attachment.id}`}
                      variant="ghost"
                      size="icon"
                      aria-label={`Fjern ${attachment.fileName}`}
                    >
                      <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                    </CleButton>
                  }
                >
                  <h3 className="mb-0 text-center">
                    Du er ved at fjerne et bilag
                  </h3>
                  <p className="mb-0 text-center">{attachment.fileName}</p>
                </ConfirmationModal>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canUpload ? (
        <>
          <div className="cle-course-certificate-document__attachment-drop mt-3">
            <CleButton
              id={texts.addButtonId}
              variant="outline"
              size="sm"
              disabled={uploadMutation.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} aria-hidden strokeWidth={1.75} />
              {attachments.length === 0 ? "Vælg fil" : "Tilføj flere"}
            </CleButton>
            <p className="text-muted small mb-0">
              Træk filer hertil · PDF · maks. 5 MB · op til {MAX_ATTACHMENTS}{" "}
              bilag
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="visually-hidden"
            onChange={handleFileInputChange}
          />
          <p className="cle-course-certificate-document__attachment-note mb-0 mt-3">
            {texts.uploadNote}
          </p>
        </>
      ) : (
        <p className="text-muted small mb-0 mt-3">
          {getAttachmentsClosedMessage(evidence, kind)}
        </p>
      )}
    </RoundedCard>
  );
};

export default SupplementaryDocumentationCard;
